import { computed } from 'vue'

import raceList from '@/data/races'
import classList from '@/data/classes'
import weaponList from '@/data/weapons'
import equipmentList from '@/data/equipment'
import difficultyList from '@/data/difficulties'
import featureList from '@/data/features'
import spellList from '@/data/spells'
import actionList from '@/data/actions'
import bonusList from '@/data/bonuses'

export default function (character) {
  const difficulty = computed(() => difficultyList[character.difficulty])

  const race = computed(() => raceList[character.race])

  const characterClass = computed(() => character.className === 'adventurer' 
    ? classList.adventurer.difficulty.beginner 
    : classList[character.className].difficulty[character.difficulty]
  )

  const className = computed(() => classList[character.className].name)

  const mainHandWeaponChoices = computed(() => {
    const seen = {}
    return [ ...characterClass.value.weaponChoices, ...(race.value?.weaponChoices || []) ]
      .reduce((acc, tag) => {
        if (weaponList[tag]) {
          acc.push(tag)
        } else {
          const weaponKeys = Object.keys(weaponList).filter((key) => {
            const weapon = weaponList[key]
            return !weapon.tags.includes('shield') && weapon.tags.includes(tag)
          })
          Array.prototype.push.apply(acc, weaponKeys)
        }
        return acc
      }, [])
      .filter((weapon) => {
        return seen[weapon] ? false : (seen[weapon] = true)
      })
  })
  
  const offHandWeaponChoices = computed(() => {
    const seen = {}
    return [ ...characterClass.value.weaponChoices, ...(race.value?.weaponChoices || []) ]
      .reduce((acc, tag) => {
        if (weaponList[tag]) {
          acc.push(tag)
        } else {
          const weaponKeys = Object.keys(weaponList).filter((key) => {
            const weapon = weaponList[key]
            return (!weapon.tags.includes('twoHand') || (weapon.tags.includes('twoHand') && weapon.tags.includes('oneHand'))) && weapon.tags.includes(tag)
          })
          Array.prototype.push.apply(acc, weaponKeys)
        }
        return acc
      }, [])
      .filter((weapon) => {
        return seen[weapon] ? false : (seen[weapon] = true)
      })
  })

  const armorChoices = computed(() => {
    const seen = {}
    return [ ...characterClass.value.armorChoices, ...(race.value?.armorChoices || []) ]
      .reduce((acc, tag) => {
        if (equipmentList[tag]) {
          acc.push(tag)
        } else {
          const equipmentKeys = Object.keys(equipmentList).filter((key) => {
            const equipment = equipmentList[key]
            return equipment.tags.includes('armor') && equipment.tags.includes(tag)
          })
          Array.prototype.push.apply(acc, equipmentKeys)
        }
        return acc
      }, [])
      .filter((equipment) => {
        return seen[equipment] ? false : (seen[equipment] = true)
      })
  })

  const baseAbilities = computed(() => Object.keys(characterClass.value.abilities).reduce((acc, ability) => {
    acc[ability] = characterClass.value.abilities[ability] + ((race.value.abilities && race.value.abilities[ability]) || 0)
    return acc
  }, {}))

  const bonuses = computed(() => {
    const allItems = [
      ...(race.value.features?.map((key) => ({ key, ...featureList[key] })) || []),
      ...(characterClass.value.features?.map((key) => ({ key, ...featureList[key] })) || []),
      ...character.features.map((key) => ({ key, ...featureList[key] })).filter((item) => !item.bonus?.toggle),
      ...character.activeEffects.map((key) => ({ key, ...(spellList[key] || featureList[key] || equipmentList[key] || actionList[key]) })),
      ...character.equippedArmor.map((key) => ({ key, ...(weaponList[key] || equipmentList[key]) })),
      ...character.equipment.map((key) => ({ key, ...equipmentList[key] })).filter((item) => item.bonus?.passive)
    ].filter((item) => item.bonus)

    const findBonus = (key) => allItems.reduce((acc, item) => {
      let value = item.bonus[key] || 0
      
      if (Array.isArray(value)) {
        value = value.reduce((newValue, part) => {
          if (typeof part === 'number') {
            newValue += part
          } else {
            switch (part) {
              case 'str':
              case 'dex':
              case 'con':
              case 'int':
              case 'wis':
              case 'cha':
                newValue += baseAbilities.value[part]
                break
              case 'prof':
                newValue += difficulty.value.profBonus
                break
              case 'lvl':
                newValue += difficulty.value.level
                break
            }
          }

          return newValue
        }, 0)
      }

      return acc + value
    }, 0)

    const elementalForces = [
      'acidResistance',
      'coldResistance',
      'fireResistance',
      'lightningResistance',
      'necroticResistance',
      'poisonResistance',
      'psychicResistance',
      'radiantResistance',
      'thunderResistance'
    ]
    const elementalResistance = findBonus('elementalResistance')

    const physicalForces = [
      'bludgeoningResistance',
      'piercingResistance',
      'slashingResistance',
      'forceResistance'
    ]
    const physicalResistance = findBonus('physicalResistance')

    return Object.keys(bonusList).reduce((acc, key) => {
      acc[key] = findBonus(key)
      if (elementalForces.includes(key)) acc[key] += elementalResistance
      if (physicalForces.includes(key)) acc[key] += physicalResistance
      return acc
    }, {})
  })

  const abilities = computed(() => Object.keys(characterClass.value.abilities).reduce((acc, ability) => {
    acc[ability] = characterClass.value.abilities[ability] 
    acc[ability] += (race.value.abilities && race.value.abilities[ability]) || 0 
    acc[ability] += bonuses.value[ability]
    return acc
  }, {}))

  const characterFeatures = computed(() => ([
    ...character.features,
    ...(characterClass.value.features || [])
  ]))

  const armor = computed(() => (
    character.equippedArmor
      .map((key) => weaponList[key] || equipmentList[key])
      .reduce((acc, item) => acc + item.armor.amount, 10 + abilities.value.dex + bonuses.value.armor)
  ))

  const maxHealth = computed(() => characterClass.value.health[0] + abilities.value[characterClass.value.health[1]] + bonuses.value.maxHealth)

  return {
    race,
    characterClass,
    className,
    mainHandWeaponChoices,
    offHandWeaponChoices,
    armorChoices,
    difficulty,
    bonuses,
    abilities,
    characterFeatures,
    armor,
    maxHealth
  }
}
